import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Container,
  NativeSelect,
  Alert,
  Select,
  Text,
  TextInput,
  Button,
  Group,
  Stack,
  Center,
  Radio,
  Paper,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { DataContext } from "../../../context/DataContext";
import {
  IconShieldCheck,
  IconShieldCancel,
  IconPhoneCheck,
  IconArrowLeft,
} from "@tabler/icons-react";
import "./style.css";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import useLocale from "../../../hooks/useLocale";
import DataCollectionForm from "../../../components/data-collection-form";
import VerificationProcessingScreen from "../../../components/verification-processing-screen";
import { getEdgeQueueUrl } from "../../../utils/edge";
export default function IDMatchPlus() {
  const { data, settings, text, dispatch } = useContext(DataContext);
  const locale = useLocale("idmatchplus");
  const [isQuiz, setIsQuiz] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [template, setTemplate] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [animation, setAnimation] = useState("");
  const [gettingNextQuestion, setGettingNextQuestion] = useState(false);
  const [isQuestionsSubmitted, setIsQuestionsSubmitted] = useState(false);

  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;

  // Terminal status captured from polling — the processing screen
  // waits for resultReady to advance to "Completing verification…"
  // and only then fires onComplete which navigates.
  const [terminalStatus, setTerminalStatus] = useState(null);

  const captureTerminal = useCallback((result) => {
    if (!result) return true;
    const status = result.status;
    const processState = result.process_state;
    if (processState === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      return false;
    }
    if (processState === "COMPLETED") {
      setTerminalStatus(status === "PASS" ? "PASS" : "FAIL");
      return false;
    }
    if (status === "PASS") {
      setTerminalStatus("PASS");
      return false;
    }
    if (status === "FAIL") {
      setTerminalStatus("FAIL");
      return false;
    }
    return true;
  }, []);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    dispatch({ type: "SET_STATUS", payload: terminalStatus || "FAIL" });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch]);

  useStatusCheck(!isEdgeMode && scoring, false, "idmatch/plus", captureTerminal);
  useEdgeStatusCheck(isEdgeMode && scoring, captureTerminal);

  // Get localized text with English fallbacks
  const t = {
    form: {
      title: locale?.form?.title || "Challenge Questions",
      subtitle: locale?.form?.subtitle || "Enter your accurate details, then answer a few questions for the highest chance of success.",
      buttonText: locale?.form?.buttonText || "Start Verification",
      loadingText: locale?.form?.loadingText || "Getting Questions...",
      fields: {
        firstName: {
          label: locale?.form?.fields?.firstName?.label || "First Name",
          errorMessage: locale?.form?.fields?.firstName?.errorMessage || "First name must have at least 2 letters",
        },
        lastName: {
          label: locale?.form?.fields?.lastName?.label || "Last Name",
          errorMessage: locale?.form?.fields?.lastName?.errorMessage || "Last name must have at least 2 letters",
        },
        address: {
          label: locale?.form?.fields?.address?.label || "Address",
          streetLabel: locale?.form?.fields?.address?.streetLabel || "Street Address",
          cityLabel: locale?.form?.fields?.address?.cityLabel || "City",
          stateLabel: locale?.form?.fields?.address?.stateLabel || "State",
          zipLabel: locale?.form?.fields?.address?.zipLabel || "Zip",
          zipCodeLabel: locale?.form?.fields?.address?.zipCodeLabel || "Zip Code",
          errorMessage: locale?.form?.fields?.address?.errorMessage || "Address is required",
        },
        phone: {
          label: locale?.form?.fields?.phone?.label || "Phone Number",
          errorMessages: {
            required: locale?.form?.fields?.phone?.errorMessages?.required || "Phone number is required",
            invalid: locale?.form?.fields?.phone?.errorMessages?.invalid || "Phone number must be 10 digits",
          },
        },
      },
    },
    quiz: {
      header: locale?.quiz?.header || "Identity verification",
      submitButtonText: locale?.quiz?.submitButtonText || "Submit Answers",
      nextButtonText: locale?.quiz?.nextButtonText || "Next",
      previousButtonText: locale?.quiz?.previousButtonText || "Previous",
    },
  };

  const matchedRoute = settings?.journey?.route?.find(
    (route) => route.path === "idmatch/plus"
  );
  const routeInputs = matchedRoute?.inputs;

  const fieldMap = {
    addr: {
      name: "address",
      label: t.form.fields.address.label,
      type: "address",
      showCityState: false,
      streetLabel: t.form.fields.address.streetLabel,
      cityLabel: t.form.fields.address.cityLabel,
      stateLabel: t.form.fields.address.stateLabel,
      zipLabel: t.form.fields.address.zipLabel,
      zipCodeLabel: t.form.fields.address.zipCodeLabel,
      errorMessage: t.form.fields.address.errorMessage,
      validation: (value) => {
        if (value.addr && value.zip?.length === 5) return null;
        return t.form.fields.address.errorMessage;
      },
    },
    zip: {
      name: "zip",
      label: t.form.fields.address.zipCodeLabel,
      initialValue: "",
      maxLength: 5,
      inputMode: "numeric",
      validation: (value) =>
        value?.length !== 5 ? t.form.fields.address.errorMessage : null,
    },
    phone: {
      name: "phone",
      label: t.form.fields.phone.label,
      initialValue: "",
      validation: (value) => {
        if (!value) return t.form.fields.phone.errorMessages.required;
        const digitsOnly = value.replace(/\D/g, "");
        return digitsOnly.length !== 10 ? t.form.fields.phone.errorMessages.invalid : null;
      },
    },
    ssn: {
      name: "ssn",
      label: text?.general?.lastFourSSN || "Last 4 digits of your SSN",
      initialValue: "",
      isMaskedInput: true,
      type: "tel",
      maxLength: 4,
      pattern: "[0-9]*",
      inputMode: "numeric",
      validation: (value) => {
        if (/[^0-9]/.test(value)) return "SSN must contain only numbers";
        if (value.length !== 4) return text?.general?.lastFourSSNError || "SSN must be 4 digits";
        return null;
      },
    },
  };

  const alwaysFields = [
    {
      name: "fn",
      label: t.form.fields.firstName.label,
      initialValue: "",
      validation: (value) =>
        value.length < 2 ? t.form.fields.firstName.errorMessage : null,
    },
    {
      name: "ln",
      label: t.form.fields.lastName.label,
      initialValue: "",
      validation: (value) =>
        value.length < 2 ? t.form.fields.lastName.errorMessage : null,
    },
  ];

  const defaultExtraFields = [fieldMap.addr, fieldMap.phone];

  const extraFields = Array.isArray(routeInputs) && routeInputs.length > 0
    ? routeInputs.map((key) => fieldMap[key]).filter(Boolean)
    : defaultExtraFields;

  const formConfig = {
    fields: [...alwaysFields, ...extraFields],
  };

  const handleApiResponse = (v) => {
    const questions = v.idr5_response?.output?.questions?.questions;

    if (Array.isArray(questions) && questions.length > 0) {
      setQuestions(questions); // Store the whole question objects
      setIsQuiz(true);
      setTemplate(v.idr5_response?.continuations?.questions?.template);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setGettingNextQuestion(true);
      setAnimation("question-exit-active");
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setAnimation("question-enter-active");
        setGettingNextQuestion(false);
      }, 500); // The delay should match your animation duration
    } else {
      setIsQuestionsSubmitted(true);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setGettingNextQuestion(true);
      setAnimation("question-exit-active-back");
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
        setAnimation("question-enter-active-back");
        setGettingNextQuestion(false);
      }, 500); // The delay should match your animation duration
    }
  };

  const handleRadioChange = (value) => {
    const questionId = questions[currentQuestionIndex].id;
    setSelectedAnswers({
      ...selectedAnswers,
      [questionId]: [value], // The value is directly the selected answer's text
    });
  };

  const continueKba = async () => {
    setScoring(true);
    data.isContinue = true;
    data.template = template;
    data.template.answers = selectedAnswers;
    
    // Prepare request data
    let requestData = { ...data };
    let apiUrl;
    
    // Check if edge mode is enabled
    if (data?.edge === true) {
      const edgeQueueUrl = getEdgeQueueUrl();
      apiUrl = edgeQueueUrl;
      // Add queue parameter for idmatchplus (as updated in data-collection-form)
      requestData.queue = "idmatchplus";
    } else {
      apiUrl = `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/idmatchPlus`;
    }
    
    try {
      const response = await fetch(
        apiUrl,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestData),
        }
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          //console.log("here is the data", data);
        });
    } catch (error) {
      alert("Something Went Wrong! Please try again.");
    }
  };

  if (scoring) {
    return (
      <VerificationProcessingScreen
        type="datamatch"
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }
  if (questions.length > 0) {
    const currentQuestion = questions[currentQuestionIndex];
    const currentQuestionId = questions[currentQuestionIndex].id;
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        {gettingNextQuestion && (
          <Center
            style={{
              position: "absolute",
              top: 0,
              insetInlineEnd: 0,
              bottom: 0,
              insetInlineStart: 0,
            }}
          >
            <Loader variant="dots" />
            {/* Or use a Spinner from Mantine */}
            {/* <Loader /> */}
          </Center>
        )}
        <Box className={`question-container ${animation}`}>
          <Stack mb="xl">
            <Group>
              <Text fz="sm" tt="uppercase" fw={500}>
                {t.quiz.header}
              </Text>
              <Text fz="xs" c="dimmed">
                {currentQuestionIndex + 1} / 4
              </Text>
            </Group>

            <Text fz={25} fw={700}>
              {currentQuestion.prompt}
            </Text>
          </Stack>
          <Paper withBorder p="md" style={{ backgroundColor: data?.styles?.components?.paper?.backgroundColor ?? "#fafafa" }}>
            <Radio.Group
              value={
                selectedAnswers[currentQuestionId]
                  ? selectedAnswers[currentQuestionId][0]
                  : null
              }
              onChange={handleRadioChange} // Passing the handler directly
            >
              {currentQuestion.answers.map((answer, index) => (
                <Radio
                  mt={index > 0 ? 20 : 0}
                  size="md"
                  key={index}
                  value={answer.text}
                  label={answer.text}
                />
              ))}
            </Radio.Group>
          </Paper>
        </Box>
        <Box>
          <Stack
            spacing={5}
            //position={currentQuestionIndex > 0 ? "apart" : "right"}
            mt="md"
          >
            <Button
              size="lg"
              onClick={currentQuestionIndex === 3 ? continueKba : handleNext}
              disabled={
                !selectedAnswers[currentQuestionId] ||
                selectedAnswers[currentQuestionId].length === 0
              }
            >
              {currentQuestionIndex === 3 ? t.quiz.submitButtonText : t.quiz.nextButtonText}
            </Button>
            {currentQuestionIndex > 0 && (
              <Button
                size="xs"
                variant="subtle"
                leftIcon={<IconArrowLeft size={14} className="rtl-mirror" />}
                onClick={handleBack}
                disabled={currentQuestionIndex === 0}
              >
                {t.quiz.previousButtonText}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <DataCollectionForm
      config={formConfig}
      title={t.form.title}
      subtitle={t.form.subtitle}
      buttonText={t.form.buttonText}
      loadingText={t.form.loadingText}
      icon={
        <>
          <IconShieldCheck size={40} stroke={1.5} color="teal" />
        </>
      }
      componentKey={"idmatch/plus"}
      endpoint="/v3/stable/api/frontend/idmatchPlus"
      reAttempts={1}
      onApiResponse={(v) => handleApiResponse(v)}
    />
  );
}
